<template>
  <div class="home-layout">
    <Header />
    <div id="container" :class="{ 'container-closed': !toggleMenu }">
      <Sidebar id="menu" :class="{ 'container-aside-closed': !toggleMenu }" />
      <main :class="{ 'container-main-closed': !toggleMenu }">
        <BreadCrumbAut />
        <router-view></router-view>
      </main>
      <!-- Chatbot floating button and window, outside main for better positioning -->
      <div class="chatbot-fab-container">
        <transition name="fade">
          <div v-if="isChatBotOpened" class="chatbot-window">
            <Chat
              v-if="
                [
                  'ebf7bd1e-f4bd-4cd5-bc78-d08a40d42c44',
                  '2965fc44-bf16-492f-b479-6473b669b2d6',
                  'b7f6cc76-8403-4626-b80e-2e9ec8881768',
                  '9a0a7eb8-abfb-4e7c-baa5-4e5d366514b4',
                  '9a60ef92-132e-4ec8-8928-2d898cc6da55',
                ].includes(user?.employeeId ?? '')
              "
            />
            <iframe
              v-else
              :src="chatbotUrl"
              name="codyai"
              scrolling="yes"
              frameborder="1"
              marginheight="0"
              marginwidth="0"
              allowfullscreen
              class="chat-bot border-round shadow-3 fadein"
            ></iframe>
          </div>
        </transition>
        <Button
          id="chat-button"
          class="text-sm chatbot-fab"
          rounded
          severity="secondary"
          @click="isChatBotOpened = !isChatBotOpened"
        >
          <i class="pi pi-comments fadein text-sm" v-if="!isChatBotOpened"></i>
          <i class="pi pi-times fadein text-sm" v-else></i>
        </Button>
      </div>
    </div>
    <div
      :class="{ 'background-menu': !toggleMenu || isChatBotOpened }"
      @click="handleBackgroundClick"
    ></div>
  </div>
</template>
<script lang="ts" setup>
  import Chat from '@/shared/components/chat/Chat.vue'
  import Sidebar from '@/shared/components/Sidebar.vue'
  import Header from '@/shared/components/Header.vue'
  import BreadCrumbAut from '@/shared/components/BreadCrumbAut.vue'
  import { onMounted, ref } from 'vue'
  import { ObservableService } from '@/shared/services'
  import { storeToRefs } from 'pinia'
  import { useUserStore } from '@/store'

  const isChatBotOpened = ref(false)
  const obsService = ObservableService.getInstance()
  const toggleMenu = ref(true)
  const { user } = storeToRefs(useUserStore())
  const chatbotUrl = ref(
    'https://dashboard.nqartificial.com/sharebot_chat_NQA?id=673e032beebe211fd65f3cee'
  )
  onMounted(() => {
    if (user.value?.employeeId && user.value?.idToken) {
      chatbotUrl.value =
        chatbotUrl.value +
        `&id_employee=${user.value.employeeId}&token=${user.value.idToken}`
    }

    obsService.getObservable('toggle-menu')?.subscribe((value) => {
      if (value !== undefined && value !== null) toggleMenu.value = value
    })
  })

  const closeMenu = () => {
    toggleMenu.value = !toggleMenu.value
    obsService.emitValue('toggle-menu', toggleMenu.value)
  }

  const handleBackgroundClick = () => {
    if (isChatBotOpened.value) {
      isChatBotOpened.value = false
    } else {
      closeMenu()
    }
  }
</script>
<style scoped>
  .home-layout {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background-color: #fafafa;
  }

  .container-closed {
    grid-template-columns: 0fr 2.5fr 2.5fr !important;
  }

  .container-aside-closed {
    margin-left: -200px;
  }

  #container {
    width: 100%;
    transition: all 0.3s ease-in-out;
    display: grid;
    grid-template-columns: 1fr 2.5fr 2.5fr;
    margin-top: calc(40px + 1rem);
    position: relative;
  }

  #menu {
    grid-column: 1 / span 1;
  }

  main {
    width: 100%;
    display: flex;
    grid-column: 2 / span 2;
    flex-direction: column;
    padding-top: 30px;
    padding-left: 30px;
    height: calc(100vh - 40px - 1rem);
    background-color: #fafafa;
    overflow: auto;
  }

  footer {
    background-color: #f0f0f0;
    padding: 20px;
  }

  /* Chatbot floating action button and window */
  .chatbot-fab-container {
    position: fixed;
    z-index: 98;
    right: 2rem;
    bottom: 2rem;
    display: flex;
    flex-direction: column;
    align-items: flex-end;
  }

  .chatbot-fab {
    margin-top: 0.5rem;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
  }

  .chatbot-window {
    margin-bottom: 0.5rem;
    pointer-events: auto;
    background: transparent;
    /* For smooth animation */
    transition: all 0.2s;
    max-width: 380px;
    width: 90vw;
    min-width: 260px;
    /* Responsive height */
    height: 600px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    box-shadow: 0 4px 32px rgba(0, 0, 0, 0.18);
    background: #fafafa;
    overflow: hidden;
  }

  .chat-bot {
    width: 100%;
    height: 100%;
    min-height: 350px;
    background: #fafafa;
  }

  /* Fade transition for chatbot window */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s;
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .background-menu {
    display: none;
    position: absolute;
    z-index: 97;
    width: 100%;
    height: 100%;
    background-color: #212121;
    opacity: 0.46;
  }

  @media (max-width: 1200px) {
    #container {
      grid-template-columns: 1.5fr 2.5fr 2.5fr;
    }
    .chatbot-window {
      max-width: 340px;
      height: 500px;
    }
  }

  @media (max-width: 900px) {
    #container {
      grid-template-columns: 1.8fr 2.5fr 2.5fr;
    }
    .chatbot-window {
      max-width: 400px;
      height: 420px;
    }
  }

  @media (max-width: 800px) {
    main {
      grid-column: 1 / span 3;
    }

    .container-aside-closed {
      margin-left: 0px;
    }

    .background-menu {
      display: block;
    }
    .chatbot-fab-container {
      right: 1rem;
      bottom: 1rem;
    }
    .chatbot-window {
      max-width: 98vw;
      width: 90vw;
      min-width: 0;
      height: 60vh;
      min-height: 320px;
    }
  }

  @media (max-width: 600px) {
    .chatbot-fab-container {
      right: 0.5rem;
      bottom: 0.5rem;
    }
    .chatbot-window {
      max-width: 100vw;
      min-width: 0;
      height: 55vh;
      min-height: 220px;
      border-radius: 8px;
    }
    .chat-bot {
      border-radius: 8px;
    }
  }

  @media (max-height: 850px) {
    .chatbot-window {
      height: 450px;
      max-height: 70vh;
    }
    .chat-bot {
      height: 100%;
    }
  }

  @media (max-height: 750px) {
    .chatbot-window {
      height: 350px;
      max-height: 60vh;
    }
    .chat-bot {
      height: 100%;
    }
  }

  @media (max-height: 650px) {
    .chatbot-window {
      height: 600px;
      width: 95vw;
      max-height: 72vh;
    }
    .chat-bot {
      height: 100%;
    }
  }
</style>
